import type { WorldImage, WorldImageKind } from "@dramapie/shared";

/**
 * 世界节点「视觉设定」的分组与用途文案（纯函数，无 React / 网络）。
 *
 * 分组是**展示层**的约定：存储里只有 `kind`，生成时也只认 `kind === "全景"`。
 * 全景 / 站位两种图有明确的用途差异，所以每组带一句 hint（空态与分组头都要用）。
 */

export interface WorldImageGroupMeta {
  key: "panorama" | "landscape" | "direction" | "blocking";
  label: string;
  kinds: readonly WorldImageKind[];
  /** 用途说明；不需要解释的组给 null。 */
  hint: string | null;
}

export const WORLD_IMAGE_GROUPS: readonly WorldImageGroupMeta[] = [
  { key: "panorama", label: "全景", kinds: ["全景"], hint: "该场所有镜头共用这张背景" },
  { key: "landscape", label: "景观", kinds: ["景观"], hint: null },
  { key: "direction", label: "正反左右", kinds: ["正", "反", "左", "右"], hint: null },
  { key: "blocking", label: "站位", kinds: ["站位"], hint: "只在提示词里描述人物位置，不作为参考图" },
];

/** 「其他视角」下拉：全景有主按钮，不在这里重复。 */
export const WORLD_OTHER_IMAGE_KINDS: readonly WorldImageKind[] = ["景观", "正", "反", "左", "右", "站位"];

export interface WorldImageItem {
  image: WorldImage;
  /** 在 `node.images` 里的**原始下标**：删除接口按下标，分组不能把它拍平。 */
  index: number;
  src: string;
}

export interface WorldImageGroup {
  meta: WorldImageGroupMeta;
  items: WorldImageItem[];
}

/** 只认投影给的签名 URL；不拿 R2 key 拼地址（服务端 fail-closed 时就是没图）。 */
export function worldImageSrc(image: WorldImage): string | null {
  return typeof image.url === "string" && /^https?:\/\//i.test(image.url) ? image.url : null;
}

/**
 * 按 kind 分组（顺序固定为 全景 / 景观 / 正反左右 / 站位），空组不返回。
 * 没有签名 URL 的图不进画廊（但下标仍然指向原数组，删除不受影响）。
 */
export function worldImageGroups(images: readonly WorldImage[]): WorldImageGroup[] {
  const groups: WorldImageGroup[] = WORLD_IMAGE_GROUPS.map((meta) => ({ meta, items: [] }));
  images.forEach((image, index) => {
    const src = worldImageSrc(image);
    if (!src) return;
    const group = groups.find((item) => item.meta.kinds.includes(image.kind)) ?? groups[0];
    if (!group) return;
    group.items.push({ image, index, src });
  });
  return groups.filter((group) => group.items.length > 0);
}

/** 分组头的张数文案（全景是主图，单独说「主背景」）。 */
export function worldImageGroupNote(group: WorldImageGroup): string {
  if (group.meta.key === "panorama") return "主背景";
  return `${group.items.length} 张`;
}
